import { PublicNav } from "@/components/PublicNav";
import { SEO } from "@/components/SEO";
import type { Language } from "@/types/i18n";
import { getLocaleString } from "@/utils/dateLocalization";
import {
  Activity,
  ArrowRight,
  Brain,
  Calendar,
  Clock,
  Globe,
  Heart,
  Pill,
  QrCode,
  RefreshCw,
  Shield,
  Stethoscope,
  User,
  Wind,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  category: string;
  tags: string[];
  image_url: string | null;
  published_at: string;
  read_time_min: number;
  meta_title: string | null;
  meta_desc: string | null;
  keywords: string[];
  language: string;
  primary_keyword: string | null;
}

// Category-based fallback visuals when no image is uploaded
const CATEGORY_STYLES: Record<
  string,
  { gradient: string; icon: React.ReactNode }
> = {
  "Chronic Illness": {
    gradient: "from-rose-500 to-pink-600",
    icon: <Heart className="h-12 w-12 text-white/80" />,
  },
  "Travel Health": {
    gradient: "from-emerald-500 to-teal-600",
    icon: <Stethoscope className="h-12 w-12 text-white/80" />,
  },
  "Travel Safety": {
    gradient: "from-amber-500 to-orange-600",
    icon: <Shield className="h-12 w-12 text-white/80" />,
  },
  "Destination Health": {
    gradient: "from-primary-500 to-primary-600",
    icon: <Globe className="h-12 w-12 text-white/80" />,
  },
  Technology: {
    gradient: "from-violet-500 to-purple-600",
    icon: <QrCode className="h-12 w-12 text-white/80" />,
  },
  default: {
    gradient: "from-primary-500 to-primary-600",
    icon: <Activity className="h-12 w-12 text-white/80" />,
  },
};

// Keyword-based refinement for specific post themes
function getCoverStyle(post: BlogPost): {
  gradient: string;
  icon: React.ReactNode;
} {
  const slug = post.slug || "";
  const kw = post.primary_keyword?.toLowerCase() || "";

  if (
    slug.includes("diabetes") ||
    kw.includes("diabetes") ||
    kw.includes("insulin")
  )
    return {
      gradient: "from-rose-500 to-pink-600",
      icon: <Pill className="h-12 w-12 text-white/80" />,
    };
  if (
    slug.includes("heart") ||
    kw.includes("heart") ||
    kw.includes("cardiac") ||
    kw.includes("pacemaker")
  )
    return {
      gradient: "from-red-500 to-rose-600",
      icon: <Heart className="h-12 w-12 text-white/80" />,
    };
  if (
    slug.includes("copd") ||
    kw.includes("copd") ||
    kw.includes("asthma") ||
    kw.includes("respiratory")
  )
    return {
      gradient: "from-cyan-500 to-teal-600",
      icon: <Wind className="h-12 w-12 text-white/80" />,
    };
  if (
    slug.includes("crohn") ||
    kw.includes("crohn") ||
    kw.includes("ibd") ||
    kw.includes("colitis")
  )
    return {
      gradient: "from-amber-500 to-orange-600",
      icon: <Activity className="h-12 w-12 text-white/80" />,
    };
  if (
    slug.includes("epilep") ||
    kw.includes("epilep") ||
    kw.includes("multiple-sclerosis") ||
    kw.includes("neurolog")
  )
    return {
      gradient: "from-violet-500 to-purple-600",
      icon: <Brain className="h-12 w-12 text-white/80" />,
    };
  if (
    slug.includes("emergency") ||
    kw.includes("emergency") ||
    kw.includes("qr code")
  )
    return {
      gradient: "from-orange-500 to-red-600",
      icon: <QrCode className="h-12 w-12 text-white/80" />,
    };
  if (slug.includes("fhir") || kw.includes("fhir"))
    return {
      gradient: "from-primary-500 to-primary-600",
      icon: <Shield className="h-12 w-12 text-white/80" />,
    };
  if (
    slug.includes("thailand") ||
    slug.includes("india") ||
    slug.includes("mexico") ||
    slug.includes("bali")
  )
    return {
      gradient: "from-primary-500 to-primary-600",
      icon: <Globe className="h-12 w-12 text-white/80" />,
    };
  if (slug.includes("medication") || kw.includes("medication"))
    return {
      gradient: "from-emerald-500 to-teal-600",
      icon: <Pill className="h-12 w-12 text-white/80" />,
    };

  const fallback = {
    gradient: "from-primary-500 to-primary-600",
    icon: <Activity className="h-12 w-12 text-white/80" />,
  };
  const match = CATEGORY_STYLES[post.category];
  return match ? { gradient: match.gradient, icon: match.icon } : fallback;
}

export function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>("");
  const categories = Array.from(new Set(posts.map((p) => p.category))).sort();

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "24" });
    if (category) params.set("category", category);

    fetch(`${API_BASE}/blog?${params}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success) setPosts(json.data.posts);
        else setError("Could not load blog posts.");
      })
      .catch(() => setError("Could not reach the server."))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Travel Health Blog – Chronic Illness, Travel Tips & Emergency Guides | HealthPass"
        description="Expert travel health guides for travelers with chronic conditions. Diabetes, heart disease, COPD, Crohn's and more. Real stories, preparation checklists, destination health briefings."
        keywords="travel health blog, traveling with diabetes, traveling with chronic illness, travel health tips, emergency medical ID, medication abroad"
        url="https://travelhealthpass.app/blog"
        canonical="https://travelhealthpass.app/blog"
      />

      <PublicNav />

      {/* Hero – cleaner, matches homepage style */}
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-primary-600 text-sm font-semibold mb-4 bg-primary-50 px-4 py-1.5 rounded-full">
            <Stethoscope className="h-4 w-4" /> Travel Health Insights
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Travel Health <span className="text-primary-600">Blog</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Expert guides for travelers with chronic conditions, destination
            health briefings, and emergency preparedness checklists.
          </p>
        </div>
      </div>

      {/* Category Filter */}
      {categories.length > 0 && (
        <div className="bg-white border-b border-gray-200 sticky top-16 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setCategory("")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${!category ? "bg-primary-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${category === c ? "bg-primary-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
            <p className="text-gray-400 text-sm">Loading articles...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 mb-4">
              <Shield className="h-8 w-8 text-red-400" />
            </div>
            <p className="text-gray-500 text-lg">{error}</p>
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-50 mb-4">
              <Stethoscope className="h-8 w-8 text-primary-400" />
            </div>
            <p className="text-gray-900 text-xl font-semibold mb-2">
              Coming Soon
            </p>
            <p className="text-gray-500 max-w-md mx-auto">
              Our editorial team is preparing expert guides for travelers with
              chronic conditions. Check back soon.
            </p>
            <Link
              to="/traveler/register"
              className="mt-6 inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm"
            >
              Get notified when we publish{" "}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        )}

        {!loading && !error && posts.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => {
              const style = getCoverStyle(post);
              return (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden
                             hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  {/* Cover */}
                  <div className="aspect-[16/9] relative overflow-hidden">
                    {post.image_url ? (
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div
                        className={`w-full h-full bg-gradient-to-br ${style.gradient} flex items-center justify-center relative`}
                      >
                        {/* Pattern overlay */}
                        <div
                          className="absolute inset-0 opacity-10"
                          style={{
                            backgroundImage:
                              "radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
                            backgroundSize: "30px 30px",
                          }}
                        />
                        {style.icon}
                      </div>
                    )}
                    {/* Category badge on cover */}
                    <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      {post.category}
                    </span>
                    {/* Read time badge */}
                    <span className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {post.read_time_min} min
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-5">
                    <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition line-clamp-2 leading-snug">
                      {post.title}
                    </h2>
                    <p className="text-gray-500 text-sm line-clamp-2 mb-4 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <User className="h-3.5 w-3.5" />
                        <span>{post.author}</span>
                        <span className="text-gray-200">·</span>
                        <Calendar className="h-3.5 w-3.5" />
                        <span>
                          {/* The date follows the post's own language, like its text. */}
                          {new Date(post.published_at).toLocaleDateString(
                            getLocaleString(post.language as Language),
                            { month: "short", day: "numeric" },
                          )}
                        </span>
                      </div>
                      <span className="text-primary-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="bg-gray-900 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Ready to Travel with Peace of Mind?
          </h2>
          <p className="text-lg text-gray-400 mb-8">
            Emergency QR code, medication tracking, multilingual health records.
            Free for life.
          </p>
          <Link
            to="/traveler/register"
            className="inline-flex items-center bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition font-semibold text-lg shadow-lg shadow-primary-600/25"
          >
            Start Free – No Credit Card
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>

      {/* Schema.org Blog */}
      {posts.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "HealthPass Travel Health Blog",
            url: "https://travelhealthpass.app/blog",
            description:
              "Expert travel health guides for travelers with chronic conditions.",
            publisher: {
              "@type": "Organization",
              name: "HealthPass",
              url: "https://travelhealthpass.app",
            },
            blogPost: posts.slice(0, 10).map((post) => ({
              "@type": "BlogPosting",
              headline: post.title,
              description: post.excerpt,
              url: `https://travelhealthpass.app/blog/${post.slug}`,
              datePublished: post.published_at,
              author: { "@type": "Organization", name: post.author },
              keywords: post.keywords?.join(", "),
              publisher: { "@type": "Organization", name: "HealthPass" },
            })),
          })}
        </script>
      )}
    </div>
  );
}
